import React from "react";
import gsap from "gsap";

import HypeboardBattlesIcon from "@/assets/svg/onboarding/hypeboard-battles.svg?react";
import Head2HeadBattlesIcon from "@/assets/svg/onboarding/head2head-battles.svg?react";
import CreateBattleIcon from "@/assets/svg/onboarding/create-battle.svg?react";
import ClimbTheRanksIcon from "@/assets/svg/onboarding/climb-the-ranks.svg?react";
import { cn } from "@/helpers/tailwind";
import { useCarousel } from "@/components/ui/carousel";

const step2Cards = [
  Head2HeadBattlesIcon,
  HypeboardBattlesIcon,
  CreateBattleIcon,
  ClimbTheRanksIcon,
];

interface OnboardingStep2Props {
  variant?: "desktop" | "mobile";
}

export const OnboardingStep2: React.FC<OnboardingStep2Props> = ({
  variant = "desktop",
}) => {
  const { api } = useCarousel();
  const cardsRef = React.useRef<(HTMLDivElement | null)[]>([]);
  const timelineRef = React.useRef<gsap.core.Timeline | null>(null);

  React.useEffect(() => {
    if (!api) return;

    // Create the timeline once
    if (!timelineRef.current) {
      timelineRef.current = gsap.timeline({ paused: true }).fromTo(
        cardsRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.75,
          stagger: 0.15,
          ease: "power4.out",
        }
      );
    }

    const handleSlideChange = () => {
      const currentSlide = api.selectedScrollSnap();

      if (currentSlide === 1) {
        // Play forward when entering the slide
        timelineRef.current?.play(0);
      } else {
        // Reverse when leaving the slide
        timelineRef.current?.reverse();
      }
    };

    api.on("select", handleSlideChange);
    return () => {
      api.off("select", handleSlideChange);
      timelineRef.current?.kill();
    };
  }, [api]);

  return (
    <div
      className={cn("grid w-full gap-3", {
        "grid-cols-2": variant === "mobile",
        "grid-cols-4": variant === "desktop",
      })}>
      {step2Cards.map((Card, index) => (
        <div
          key={index}
          ref={(el) => (cardsRef.current[index] = el)}
          className={cn("overflow-hidden rounded-xl", {
            "mx-auto aspect-[158/234] max-h-[calc(100vh/3.5)] w-auto":
              variant === "mobile",
            "w-full": variant === "desktop",
          })}>
          <Card className="h-full w-full" />
        </div>
      ))}
    </div>
  );
};
